import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_PERMS, permLabel } from "@/lib/perms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ShieldCheck, UserPlus, Pencil, MailPlus, Trash2, Ban, CheckCircle2, Eye, ClipboardList, LogIn, Factory, Columns3, Printer } from "lucide-react";
import { toast } from "sonner";
import { startImpersonation } from "@/lib/impersonate";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("tr-TR") : "-");

const PermPicker = ({ perms, setPerms }) => {
  const full = perms.includes("all");
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-3 border border-slate-200 bg-slate-50 px-4 py-3 cursor-pointer" data-testid="perm-all">
        <Checkbox checked={full} onCheckedChange={(v) => setPerms(v ? ["all"] : [])} />
        <span className="text-sm font-semibold flex items-center gap-2"><ShieldCheck size={15} className="text-emerald-600" /> Tam Yetkili (her şeyi görür ve yapar)</span>
      </label>
      <div className={`grid grid-cols-2 gap-2 ${full ? "opacity-40 pointer-events-none" : ""}`}>
        {ADMIN_PERMS.map((p) => (
          <label key={p.key} className="flex items-center gap-2.5 border border-slate-200 px-3 py-2.5 cursor-pointer hover:bg-slate-50" data-testid={`perm-${p.key}`}>
            <Checkbox
              checked={!full && perms.includes(p.key)}
              onCheckedChange={(v) => setPerms(v ? [...perms.filter((x) => x !== "all"), p.key] : perms.filter((x) => x !== p.key))}
            />
            <span className="text-sm">{p.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const statusBadge = (u) => {
  if (u.is_active === false) return <Badge className="rounded-none bg-red-500/10 text-red-600 border-red-500/20" variant="outline">Pasif</Badge>;
  if (!u.password_set) return <Badge className="rounded-none bg-amber-500/10 text-amber-600 border-amber-500/20" variant="outline">Davet Edildi</Badge>;
  return <Badge className="rounded-none bg-emerald-500/10 text-emerald-600 border-emerald-500/20" variant="outline">Aktif</Badge>;
};

const SELECTABLE_COLS = [
  ["zone", "Bölge"], ["stand_no", "Stand"], ["area", "m²"], ["type", "Tür"], ["stand_type", "Stand Türü"],
  ["tasarim", "Tasarım"], ["uretim", "Üretim"], ["baski", "Baskı"], ["elektrik", "Elektrik"], ["baski_sureci", "Baskı Süreci"], ["icmal", "İcmal"],
  ["status", "Statü"], ["hub", "Hub"], ["producer", "Üretici"], ["sem", "SEM"], ["cm", "CM"],
  ["prod_status", "Üretim Durumu"], ["kurulum", "Kurulum Tarihi"], ["progress", "İlerleme"],
  ["sale", "Süreç Durum"], ["sup_status", "Tedarikçi Süreç"], ["offer", "Teklif Tutarı"], ["winner", "Kazanan Tedarikçi"],
];

const ALL_COL_KEYS = SELECTABLE_COLS.map(([k]) => k);

const ColsPicker = ({ value, onChange, editValue, onEditChange, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Görebileceği Sütunlar <span className="normal-case font-normal text-slate-400">— sol kutu: görür · yeşil kutu: düzenler</span></span>
      <span className="flex gap-2">
        <button type="button" className="text-[11px] text-blue-600 hover:underline" onClick={() => { onChange(ALL_COL_KEYS); onEditChange(ALL_COL_KEYS); }}>Tümünü seç</button>
        <button type="button" className="text-[11px] text-slate-400 hover:underline" onClick={() => { onChange([]); onEditChange([]); }}>Temizle</button>
      </span>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 border border-slate-200 p-3 max-h-[220px] overflow-y-auto">
      {SELECTABLE_COLS.map(([k, label]) => (
        <label key={k} className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer py-0.5">
          <Checkbox
            checked={value.includes(k)}
            onCheckedChange={(c) => { onChange(c ? [...value, k] : value.filter((x) => x !== k)); if (!c) onEditChange(editValue.filter((x) => x !== k)); }}
            className="rounded-none"
            title="Görür"
            data-testid={`col-check-${k}`}
          />
          <Checkbox
            checked={editValue.includes(k)}
            disabled={!value.includes(k)}
            onCheckedChange={(c) => onEditChange(c ? [...editValue, k] : editValue.filter((x) => x !== k))}
            className="rounded-none border-emerald-400 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
            title="Düzenleyebilir"
            data-testid={`col-edit-${k}`}
          />
          {label}
        </label>
      ))}
    </div>
    <p className="text-[11px] text-slate-400 mt-1">Yeşil kutu o sütunda düzenlemeye izin verir ("Düzenleyebilir" açıkken geçerli). Hiçbiri seçilmezse tüm sütunları görür. Proje adı her zaman görünür.</p>
  </div>
);

const ViewersSection = () => {
  const { user: authUser } = useAuth();
  const fullAdmin = (authUser?.permissions || ["all"]).includes("all");
  const [viewers, setViewers] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [cols, setCols] = useState(ALL_COL_KEYS);
  const [editColsSel, setEditColsSel] = useState(ALL_COL_KEYS);
  const [canEditNew, setCanEditNew] = useState(false);
  const [seeAllNew, setSeeAllNew] = useState(false);
  const [zonesNew, setZonesNew] = useState(["Blue Zone", "Green Zone"]);
  const [editAcc, setEditAcc] = useState(null);
  const [editCols, setEditCols] = useState([]);
  const [editCanEdit, setEditCanEdit] = useState(false);
  const [editSeeAll, setEditSeeAll] = useState(false);
  const [editAccEditCols, setEditAccEditCols] = useState([]);
  const [editZones, setEditZones] = useState([]);

  const load = () => api.get("/viewers").then((r) => setViewers(r.data)).catch(() => {});

  const toggleNotify = async (v, c) => {
    try {
      await api.patch(`/viewers/${v.id}`, { notify_emails: c });
      setViewers((vs) => vs.map((x) => (x.id === v.id ? { ...x, notify_emails: c } : x)));
      toast.success(c ? `${v.contact_name}: bildirimler açıldı` : `${v.contact_name}: bildirimler kapatıldı`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!email.trim().includes("@")) return toast.error("Geçerli bir e-posta girin");
    if (!name.trim()) return toast.error("İsim girin");
    if (zonesNew.length === 0) return toast.error("En az bir bölge seçin");
    setBusy(true);
    try {
      await api.post("/viewers", { contact_name: name.trim(), contact_email: email.trim(), visible_cols: cols, editable_cols: editColsSel, can_edit: canEditNew, see_all: seeAllNew, zones: zonesNew });
      toast.success("Hesap oluşturuldu — şifresi e-posta ile gönderildi");
      setName(""); setEmail(""); setCols(ALL_COL_KEYS); setEditColsSel(ALL_COL_KEYS); setCanEditNew(false); setSeeAllNew(false); setZonesNew(["Blue Zone", "Green Zone"]);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggleViewerZone = async (v, z) => {
    const next = v.zones?.includes(z) ? v.zones.filter((x) => x !== z) : [...(v.zones || []), z];
    if (next.length === 0) return toast.error("En az bir bölge seçili kalmalı");
    try {
      await api.patch(`/viewers/${v.id}`, { zones: next });
      setViewers((vs) => vs.map((x) => (x.id === v.id ? { ...x, zones: next } : x)));
      toast.success(`${v.contact_name}: bölgeler güncellendi`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveAccess = async () => {
    if (editZones.length === 0) return toast.error("En az bir bölge seçin");
    try {
      await api.patch(`/viewers/${editAcc.id}`, { visible_cols: editCols, editable_cols: editAccEditCols, can_edit: editCanEdit, see_all: editSeeAll, zones: editZones });
      toast.success("Erişim güncellendi");
      setEditAcc(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const resend = async (v) => {
    try {
      await api.post(`/viewers/${v.id}/resend`);
      toast.success("Yeni şifre e-posta ile gönderildi");
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/viewers/${toDelete.id}`);
      toast.success("İzleyici silindi");
      setToDelete(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="border border-slate-200 bg-white" data-testid="viewers-section">
      <div className="px-6 py-5 border-b border-slate-200">
        <div className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
          <Eye size={18} className="text-sky-600" /> Ekip Üyeleri
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Tek tip hesap: proje tablosunu <strong>yalnızca seçtiğiniz sütunlarla</strong> görür. "Düzenleyebilir" açıksa gördüğü sütunlarda değişiklik yapabilir; kapalıysa salt-okunurdur (CM eşleşmesi varsa kendi standlarında ilerleme + kurulum tarihi işaretleyebilir).
        </p>
      </div>
      <div className="px-6 py-4 border-b border-slate-200 space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ad Soyad" data-testid="viewer-invite-name" className="rounded-none border-slate-200 h-11 sm:max-w-[220px]" />
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="kisi@firma.com" data-testid="viewer-invite-email" className="rounded-none border-slate-200 h-11 flex-1" />
          <span className="flex items-center gap-3 px-2">
            {ASSIGNER_ZONES.map((z) => (
              <label key={z} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer whitespace-nowrap">
                <Checkbox
                  checked={zonesNew.includes(z)}
                  onCheckedChange={(c) => setZonesNew((zs) => (c ? [...zs, z] : zs.filter((x) => x !== z)))}
                  className="rounded-none"
                  data-testid={`viewer-invite-zone-${z.split(" ")[0].toLowerCase()}`}
                />
                {z}
              </label>
            ))}
          </span>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer whitespace-nowrap px-2" title="Açıksa bölgesindeki tüm müşterileri görür; kapalıysa yalnız CM/SEM olarak atandığı projeleri görür">
            <Switch checked={seeAllNew} onCheckedChange={setSeeAllNew} data-testid="viewer-invite-see-all" className="scale-90" />
            Tüm müşteriler
          </label>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer whitespace-nowrap px-2">
            <Switch checked={canEditNew} onCheckedChange={setCanEditNew} data-testid="viewer-invite-can-edit" className="scale-90" />
            Düzenleyebilir
          </label>
          <Button onClick={invite} disabled={busy} data-testid="viewer-invite-button" className="rounded-none h-11 px-5 bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200">
            <UserPlus size={15} /> Hesap Oluştur
          </Button>
        </div>
        <ColsPicker value={cols} onChange={setCols} editValue={editColsSel} onEditChange={setEditColsSel} testid="viewer-invite-cols" />
      </div>
      {viewers.length === 0 ? (
        <div className="px-6 py-8 text-center text-slate-500 text-sm" data-testid="viewers-empty">Henüz izleyici yok.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {viewers.map((v) => (
            <li key={v.id} className="px-6 py-3 flex items-center gap-3 flex-wrap" data-testid={`viewer-row-${v.id}`}>
              <span className="text-sm font-medium">{v.contact_name}</span>
              <span className="text-sm text-slate-500 flex-1">{v.contact_email}</span>
              <span className="flex gap-1">
                {ASSIGNER_ZONES.map((z) => (
                  <button
                    key={z}
                    type="button"
                    onClick={() => toggleViewerZone(v, z)}
                    data-testid={`viewer-zone-${z.split(" ")[0].toLowerCase()}-${v.id}`}
                    title={v.zones?.includes(z) ? "Bölgeyi kaldır" : "Bölgeye ekle"}
                    className={`px-2 py-0.5 text-[10px] font-semibold border transition-colors duration-200 ${v.zones?.includes(z) ? zoneBadgeCls[z] : "border-slate-200 text-slate-300 hover:text-slate-500"}`}>
                    {z}
                  </button>
                ))}
              </span>
              <Badge variant="outline" className={`rounded-none text-[10px] ${v.can_edit ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                {v.can_edit ? "Düzenleyebilir" : "Salt-okunur"}
              </Badge>
              <Badge variant="outline" className={`rounded-none text-[10px] ${v.see_all ? "bg-indigo-500/10 text-indigo-600 border-indigo-500/20" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                {v.see_all ? "Tüm müşteriler" : "Atanan müşteriler"}
              </Badge>
              <Badge variant="outline" className="rounded-none text-[10px] bg-sky-500/10 text-sky-600 border-sky-500/20">
                {Array.isArray(v.visible_cols) && v.visible_cols.length > 0 ? `${v.visible_cols.length} sütun` : "Tüm sütunlar"}
              </Badge>
              <span className="text-xs text-slate-400">{fmtDate(v.created_at)}</span>
              <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer" title="İhale açılışı ve dosya yükleme e-posta bildirimleri">
                <Switch checked={v.notify_emails !== false} onCheckedChange={(c) => toggleNotify(v, c)} data-testid={`viewer-notify-${v.id}`} className="scale-75" />
                Bildirim
              </label>
              {fullAdmin && (
              <Button variant="outline" size="sm" onClick={() => startImpersonation(v.id).catch((e) => toast.error(apiError(e)))} data-testid={`viewer-impersonate-${v.id}`} title="Bu kullanıcının hesabına geç" className="rounded-none h-8 border-slate-200 hover:bg-slate-900 hover:text-white gap-1.5 text-xs transition-colors duration-200">
                <LogIn size={12} /> Hesabına Geç
              </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => { setEditAcc(v); setEditCols(Array.isArray(v.visible_cols) && v.visible_cols.length ? v.visible_cols : ALL_COL_KEYS); setEditAccEditCols(Array.isArray(v.editable_cols) ? v.editable_cols : ALL_COL_KEYS); setEditCanEdit(!!v.can_edit); setEditSeeAll(!!v.see_all); setEditZones(Array.isArray(v.zones) && v.zones.length ? v.zones : ["Blue Zone", "Green Zone"]); }} data-testid={`viewer-access-${v.id}`} title="Sütun ve yetki ayarları" className="rounded-none h-8 border-slate-200 hover:bg-slate-100 gap-1.5 text-xs transition-colors duration-200">
                <Columns3 size={12} /> Erişim
              </Button>
              <Button variant="outline" size="sm" onClick={() => resend(v)} data-testid={`viewer-resend-${v.id}`} title="Yeni şifre gönder" className="rounded-none h-8 border-slate-200 hover:bg-slate-100 gap-1.5 text-xs transition-colors duration-200">
                <MailPlus size={12} /> Şifre Gönder
              </Button>
              <button onClick={() => setToDelete(v)} data-testid={`viewer-delete-${v.id}`} title="Sil" className="text-slate-400 hover:text-red-500 transition-colors duration-200"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      )}
      <Dialog open={!!editAcc} onOpenChange={(o) => !o && setEditAcc(null)}>
        <DialogContent className="rounded-none sm:max-w-lg" data-testid="viewer-access-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editAcc?.contact_name} — Erişim Ayarları</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2">Bölge Yetkisi</div>
              <div className="flex gap-4">
                {["Blue Zone", "Green Zone"].map((z) => (
                  <label key={z} className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
                    <Checkbox
                      checked={editZones.includes(z)}
                      onCheckedChange={(c) => setEditZones((zs) => (c ? [...zs, z] : zs.filter((x) => x !== z)))}
                      data-testid={`viewer-access-zone-${z === "Blue Zone" ? "blue" : "green"}`}
                      className="rounded-none"
                    />
                    <span className={z === "Blue Zone" ? "text-blue-600" : "text-emerald-600"}>{z}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">İki bölge birden seçilebilir; kişi yalnız seçili bölgelerdeki projeleri görür.</p>
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
              <Switch checked={editCanEdit} onCheckedChange={setEditCanEdit} data-testid="viewer-access-can-edit" className="scale-90" />
              Düzenleyebilir
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
              <Switch checked={editSeeAll} onCheckedChange={setEditSeeAll} data-testid="viewer-access-see-all" className="scale-90" />
              Tüm müşterileri görsün
            </label>
            <p className="text-[11px] text-slate-400 -mt-2">Kapalıysa yalnız CM/SEM olarak atandığı projeleri görür; açıksa bölgesindeki tüm müşterileri görür.</p>
            <ColsPicker value={editCols} onChange={setEditCols} editValue={editAccEditCols} onEditChange={setEditAccEditCols} testid="viewer-access-cols" />
            <Button onClick={saveAccess} data-testid="viewer-access-save" className="w-full rounded-none bg-slate-900 text-white hover:bg-slate-700 font-semibold">Kaydet</Button>
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>İzleyici silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.contact_name} ({toDelete?.contact_email}) kalıcı olarak silinecek ve giriş yapamayacak.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-viewer" className="rounded-none bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const ASSIGNER_ZONES = ["Blue Zone", "Green Zone"];
const zoneBadgeCls = { "Blue Zone": "bg-blue-500/10 text-blue-600 border-blue-500/20", "Green Zone": "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" };

const AssignersSection = () => {
  const { user: authUser } = useAuth();
  const fullAdmin = (authUser?.permissions || ["all"]).includes("all");
  const [assigners, setAssigners] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [zones, setZones] = useState([]);
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/assigners").then((r) => setAssigners(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleFormZone = (z) => setZones((zs) => (zs.includes(z) ? zs.filter((x) => x !== z) : [...zs, z]));

  const invite = async () => {
    if (!name.trim()) return toast.error("İsim girin");
    if (!email.trim().includes("@")) return toast.error("Geçerli bir e-posta girin");
    if (zones.length === 0) return toast.error("En az bir bölge seçin");
    setBusy(true);
    try {
      await api.post("/assigners", { contact_name: name.trim(), contact_email: email.trim(), zones });
      toast.success("Atayıcı davet edildi — şifresi e-posta ile gönderildi");
      setName(""); setEmail(""); setZones([]);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggleRowZone = async (a, z) => {
    const next = a.zones?.includes(z) ? a.zones.filter((x) => x !== z) : [...(a.zones || []), z];
    if (next.length === 0) return toast.error("En az bir bölge seçili kalmalı");
    try {
      await api.patch(`/assigners/${a.id}`, { zones: next });
      setAssigners((as) => as.map((x) => (x.id === a.id ? { ...x, zones: next } : x)));
      toast.success(`${a.contact_name}: bölgeler güncellendi`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const resend = async (a) => {
    try {
      await api.post(`/assigners/${a.id}/resend`);
      toast.success("Yeni şifre e-posta ile gönderildi");
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/assigners/${toDelete.id}`);
      toast.success("Atayıcı silindi");
      setToDelete(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="border border-slate-200 bg-white" data-testid="assigners-section">
      <div className="px-6 py-5 border-b border-slate-200">
        <div className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
          <ClipboardList size={18} className="text-violet-600" /> Atayıcılar
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Atandığı bölgelerde <strong>yeni proje oluşturabilir</strong>, mevcut projelere <strong>dosya yükleyebilir</strong>, statü/hub/durum ve teklif tutarı düzenleyebilir. Proje oluşturduğunda ve dosya yüklediğinde size e-posta bildirimi gelir.
        </p>
      </div>
      <div className="px-6 py-4 border-b border-slate-200 flex flex-col sm:flex-row gap-2 sm:items-center">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ad Soyad" data-testid="assigner-invite-name" className="rounded-none border-slate-200 h-11 sm:max-w-[200px]" />
        <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="kisi@firma.com" data-testid="assigner-invite-email" className="rounded-none border-slate-200 h-11 flex-1" />
        <div className="flex items-center gap-3 shrink-0">
          {ASSIGNER_ZONES.map((z) => (
            <label key={z} className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer" data-testid={`assigner-invite-zone-${z}`}>
              <Checkbox checked={zones.includes(z)} onCheckedChange={() => toggleFormZone(z)} />
              {z}
            </label>
          ))}
        </div>
        <Button onClick={invite} disabled={busy} data-testid="assigner-invite-button" className="rounded-none h-11 px-5 bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200">
          <UserPlus size={15} /> Davet Et
        </Button>
      </div>
      {assigners.length === 0 ? (
        <div className="px-6 py-8 text-center text-slate-500 text-sm" data-testid="assigners-empty">Henüz atayıcı yok.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {assigners.map((a) => (
            <li key={a.id} className="px-6 py-3 flex items-center gap-3 flex-wrap" data-testid={`assigner-row-${a.id}`}>
              <span className="text-sm font-medium">{a.contact_name}</span>
              <span className="text-sm text-slate-500 flex-1">{a.contact_email}</span>
              <div className="flex items-center gap-1.5">
                {ASSIGNER_ZONES.map((z) => (
                  <button key={z} onClick={() => toggleRowZone(a, z)} data-testid={`assigner-zone-toggle-${a.id}-${z}`}
                    title={a.zones?.includes(z) ? "Bölgeyi kaldır" : "Bölgeye ekle"}
                    className={`px-2 py-0.5 text-[10px] font-semibold border transition-colors duration-200 ${a.zones?.includes(z) ? zoneBadgeCls[z] : "border-slate-200 text-slate-300 hover:text-slate-500"}`}>
                    {z}
                  </button>
                ))}
              </div>
              <span className="text-xs text-slate-400">{fmtDate(a.created_at)}</span>
              {fullAdmin && (
              <Button variant="outline" size="sm" onClick={() => startImpersonation(a.id).catch((e) => toast.error(apiError(e)))} data-testid={`assigner-impersonate-${a.id}`} title="Bu kullanıcının hesabına geç" className="rounded-none h-8 border-slate-200 hover:bg-slate-900 hover:text-white gap-1.5 text-xs transition-colors duration-200">
                <LogIn size={12} /> Hesabına Geç
              </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => resend(a)} data-testid={`assigner-resend-${a.id}`} title="Yeni şifre gönder" className="rounded-none h-8 border-slate-200 hover:bg-slate-100 gap-1.5 text-xs transition-colors duration-200">
                <MailPlus size={12} /> Şifre Gönder
              </Button>
              <button onClick={() => setToDelete(a)} data-testid={`assigner-delete-${a.id}`} title="Sil" className="text-slate-400 hover:text-red-500 transition-colors duration-200"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      )}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Atayıcı silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.contact_name} ({toDelete?.contact_email}) kalıcı olarak silinecek ve giriş yapamayacak.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-assigner" className="rounded-none bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const ProducersSection = () => {
  const [producers, setProducers] = useState([]);
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/producers").then((r) => setProducers(r.data)).catch(() => {});

  const saveEdit = async () => {
    if (!editing.company_name?.trim()) return toast.error("Firma adı girin");
    if ((editing.phone || "").trim().length < 5) return toast.error("Telefon girin");
    if ((editing.contact_email || "").trim() && !editing.contact_email.includes("@")) return toast.error("Geçerli bir e-posta girin");
    try {
      await api.patch(`/producers/${editing.id}`, {
        company: editing.company_name.trim(),
        contact_name: (editing.contact_name || "").trim() || null,
        phone: editing.phone.trim(),
        contact_email: (editing.contact_email || "").trim() || null,
      });
      toast.success("Üretici güncellendi");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!company.trim()) return toast.error("Firma adı girin");
    if (phone.trim().length < 5) return toast.error("Telefon girin");
    if (email.trim() && !email.includes("@")) return toast.error("Geçerli bir e-posta girin");
    setBusy(true);
    try {
      await api.post("/producers", { company: company.trim(), contact_name: name.trim() || null, phone: phone.trim(), contact_email: email.trim() || null });
      toast.success("Üretici eklendi");
      setName(""); setCompany(""); setPhone(""); setEmail("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/producers/${toDelete.id}`);
      toast.success("Üretici silindi");
      setToDelete(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="border border-slate-200 bg-white" data-testid="producers-section">
      <div className="px-6 py-5 border-b border-slate-200">
        <div className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
          <Factory size={18} className="text-amber-600" /> Üreticiler
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Üreticiler yalnızca projelere atanan kayıtlardır — <strong>giriş hesabı oluşturulmaz, e-posta gönderilmez</strong>. Proje tablolarındaki "Üretici" sütunundan atama yapılır.
        </p>
      </div>
      <div className="px-6 py-4 border-b border-slate-200 flex flex-col sm:flex-row gap-2 sm:items-center">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ad Soyad (opsiyonel)" data-testid="producer-invite-name" className="rounded-none border-slate-200 h-11 sm:max-w-[190px]" />
        <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Firma Adı" data-testid="producer-invite-company" className="rounded-none border-slate-200 h-11 sm:max-w-[200px]" />
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+90 532 4566767" data-testid="producer-invite-phone" className="rounded-none border-slate-200 h-11 sm:max-w-[180px] placeholder:text-slate-300" />
        <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-posta (opsiyonel)" data-testid="producer-invite-email" className="rounded-none border-slate-200 h-11 flex-1" />
        <Button onClick={add} disabled={busy} data-testid="producer-invite-button" className="rounded-none h-11 px-5 bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200">
          <UserPlus size={15} /> Ekle
        </Button>
      </div>
      {producers.length === 0 ? (
        <div className="px-6 py-8 text-center text-slate-500 text-sm" data-testid="producers-empty">Henüz üretici yok.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {producers.map((a) => (
            editing?.id === a.id ? (
              <li key={a.id} className="px-6 py-3 flex flex-col sm:flex-row gap-2 sm:items-center bg-slate-50" data-testid={`producer-edit-row-${a.id}`}>
                <Input value={editing.contact_name || ""} onChange={(e) => setEditing({ ...editing, contact_name: e.target.value })} placeholder="Ad Soyad (opsiyonel)" data-testid={`producer-edit-name-${a.id}`} className="rounded-none border-slate-200 h-9 text-xs sm:max-w-[180px]" />
                <Input value={editing.company_name || ""} onChange={(e) => setEditing({ ...editing, company_name: e.target.value })} placeholder="Firma Adı" data-testid={`producer-edit-company-${a.id}`} className="rounded-none border-slate-200 h-9 text-xs sm:max-w-[190px]" />
                <Input value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} placeholder="Telefon" data-testid={`producer-edit-phone-${a.id}`} className="rounded-none border-slate-200 h-9 text-xs sm:max-w-[160px]" />
                <Input value={editing.contact_email || ""} onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })} placeholder="E-posta (opsiyonel)" data-testid={`producer-edit-email-${a.id}`} className="rounded-none border-slate-200 h-9 text-xs flex-1" />
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={saveEdit} data-testid={`producer-edit-save-${a.id}`} className="rounded-none h-9 px-4 bg-slate-900 text-white hover:bg-slate-700 text-xs font-semibold transition-colors duration-200">Kaydet</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(null)} data-testid={`producer-edit-cancel-${a.id}`} className="rounded-none h-9 px-3 border-slate-200 text-xs transition-colors duration-200">Vazgeç</Button>
                </div>
              </li>
            ) : (
            <li key={a.id} className="px-6 py-3 flex items-center gap-3 flex-wrap" data-testid={`producer-row-${a.id}`}>
              <span className="text-sm font-semibold" data-testid={`producer-company-${a.id}`}>{a.company_name && a.company_name !== "Üretici" ? a.company_name : (a.contact_name || "-")}</span>
              {a.contact_name && a.company_name && a.company_name !== "Üretici" && <span className="text-sm text-slate-500">{a.contact_name}</span>}
              <span className="text-sm text-slate-500" data-testid={`producer-phone-${a.id}`}>{a.phone || "—"}</span>
              <span className="text-sm text-slate-400 flex-1">{a.contact_email || ""}</span>
              <span className="text-xs text-slate-400">{fmtDate(a.created_at)}</span>
              <button onClick={() => setEditing({ id: a.id, contact_name: a.contact_name || "", company_name: a.company_name || "", phone: a.phone || "", contact_email: a.contact_email || "" })} data-testid={`producer-edit-${a.id}`} title="Düzenle" className="text-slate-400 hover:text-slate-900 transition-colors duration-200"><Pencil size={14} /></button>
              <button onClick={() => setToDelete(a)} data-testid={`producer-delete-${a.id}`} title="Sil" className="text-slate-400 hover:text-red-500 transition-colors duration-200"><Trash2 size={14} /></button>
            </li>
            )
          ))}
        </ul>
      )}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Üretici silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.company_name && toDelete?.company_name !== "Üretici" ? toDelete.company_name : toDelete?.contact_name} kalıcı olarak silinecek ve atandığı projelerden kaldırılacak.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-producer" className="rounded-none bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const TASERON_TYPES = [["baskici", "Baskıcı"]];
const taseronTypeLabel = (t) => TASERON_TYPES.find(([k]) => k === t)?.[1] || "Baskıcı";

const TaseronsSection = () => {
  const [taserons, setTaserons] = useState([]);
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [ttype, setTtype] = useState("baskici");
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/taserons").then((r) => setTaserons(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return toast.error("Ad Soyad girin");
    if (!email.includes("@")) return toast.error("Geçerli bir e-posta girin");
    setBusy(true);
    try {
      await api.post("/taserons", { contact_name: name.trim(), contact_email: email.trim(), company_name: company.trim(), taseron_type: ttype });
      toast.success("Taşeron üretici davet edildi — giriş bilgileri e-posta ile gönderildi");
      setName(""); setCompany(""); setEmail("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const resend = async (t) => {
    try {
      await api.post(`/taserons/${t.id}/resend`);
      toast.success("Yeni şifre e-posta ile gönderildi");
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/taserons/${toDelete.id}`);
      toast.success("Taşeron silindi");
      setToDelete(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="border border-slate-200 bg-white" data-testid="taserons-section">
      <div className="px-6 py-5 border-b border-slate-200">
        <div className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
          <Printer size={18} className="text-sky-600" /> Taşeron Üreticiler
        </div>
        <p className="text-xs text-slate-500 mt-1">
          <strong>Baskıcı</strong> tipi taşeronlar tüm projelerin yalnızca <strong>baskı dosyalarını</strong> görür; üretilen baskıların fotoğraflarını yükler, üretim sürecini günceller ve teklif ekleyebilir. Giriş bilgileri e-posta ile gönderilir.
        </p>
      </div>
      <div className="px-6 py-4 border-b border-slate-200 flex flex-col sm:flex-row gap-2 sm:items-center">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ad Soyad" data-testid="taseron-invite-name" className="rounded-none border-slate-200 h-11 sm:max-w-[190px]" />
        <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Firma (opsiyonel)" data-testid="taseron-invite-company" className="rounded-none border-slate-200 h-11 sm:max-w-[190px]" />
        <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-posta" data-testid="taseron-invite-email" className="rounded-none border-slate-200 h-11 flex-1" />
        <select value={ttype} onChange={(e) => setTtype(e.target.value)} data-testid="taseron-invite-type" className="h-11 border border-slate-200 bg-white px-3 text-sm font-semibold sm:w-[150px]">
          {TASERON_TYPES.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
        </select>
        <Button onClick={add} disabled={busy} data-testid="taseron-invite-button" className="rounded-none h-11 px-5 bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200">
          <UserPlus size={15} /> Davet Et
        </Button>
      </div>
      {taserons.length === 0 ? (
        <div className="px-6 py-8 text-center text-slate-500 text-sm" data-testid="taserons-empty">Henüz taşeron üretici yok.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {taserons.map((t) => (
            <li key={t.id} className="px-6 py-3 flex items-center gap-3 flex-wrap" data-testid={`taseron-row-${t.id}`}>
              <span className="text-sm font-semibold">{t.contact_name}</span>
              {t.company_name && t.company_name !== "Taşeron" && <span className="text-sm text-slate-500">{t.company_name}</span>}
              <Badge variant="outline" className="rounded-none bg-sky-500/10 text-sky-600 border-sky-500/20 text-[10px]">{taseronTypeLabel(t.taseron_type)}</Badge>
              <span className="text-sm text-slate-400 flex-1">{t.contact_email}</span>
              {statusBadge(t)}
              <span className="text-xs text-slate-400">{fmtDate(t.created_at)}</span>
              <Button variant="outline" size="sm" onClick={() => startImpersonation(t.id).catch((e) => toast.error(apiError(e)))} data-testid={`taseron-impersonate-${t.id}`} title="Bu kullanıcının hesabına geç" className="rounded-none h-8 border-slate-200 hover:bg-slate-900 hover:text-white gap-1.5 text-xs transition-colors duration-200">
                <LogIn size={12} /> Hesabına Geç
              </Button>
              <button onClick={() => resend(t)} data-testid={`taseron-resend-${t.id}`} title="Şifreyi yeniden gönder" className="text-slate-400 hover:text-slate-900 transition-colors duration-200"><MailPlus size={14} /></button>
              <button onClick={() => setToDelete(t)} data-testid={`taseron-delete-${t.id}`} title="Sil" className="text-slate-400 hover:text-red-500 transition-colors duration-200"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      )}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Taşeron silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.contact_name} ({toDelete?.contact_email}) kalıcı olarak silinecek ve portala erişimi kapanacak.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-taseron" className="rounded-none bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [dialog, setDialog] = useState(null); // {mode:'create'} | {mode:'edit', user}
  const [form, setForm] = useState({ contact_name: "", contact_email: "", permissions: [] });
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => api.get("/admin-users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm({ contact_name: "", contact_email: "", permissions: [] }); setDialog({ mode: "create" }); };
  const openEdit = (u) => { setForm({ contact_name: u.contact_name, contact_email: u.contact_email, permissions: u.permissions || ["all"] }); setDialog({ mode: "edit", user: u }); };

  const submit = async () => {
    if (form.permissions.length === 0) return toast.error("En az bir yetki seçmelisiniz");
    setBusy(true);
    try {
      if (dialog.mode === "create") {
        await api.post("/admin-users", form);
        toast.success("Davet e-postası gönderildi");
      } else {
        await api.patch(`/admin-users/${dialog.user.id}`, { contact_name: form.contact_name, permissions: form.permissions });
        toast.success("Kullanıcı güncellendi");
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin-users/${u.id}`, { is_active: u.is_active === false });
      toast.success(u.is_active === false ? "Kullanıcı aktifleştirildi" : "Kullanıcı pasifleştirildi");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const resend = async (u) => {
    try {
      await api.post(`/admin-users/${u.id}/resend-invite`);
      toast.success("Davet e-postası yeniden gönderildi");
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin-users/${confirmDelete.id}`);
      toast.success("Kullanıcı silindi");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="admin-users-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Yönetim</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1">Kullanıcılar</h1>
          <p className="text-sm text-slate-500 mt-2">Yönetici kullanıcıları ve modül yetkilerini buradan yönetin.</p>
        </div>
        <Button onClick={openCreate} data-testid="invite-admin-button" className="rounded-none bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold h-11 px-6">
          <UserPlus size={15} /> Yeni Kullanıcı Davet Et
        </Button>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3.5">Ad Soyad</th>
              <th className="px-5 py-3.5">E-posta</th>
              <th className="px-5 py-3.5">Yetkiler</th>
              <th className="px-5 py-3.5">Durum</th>
              <th className="px-5 py-3.5">Kayıt</th>
              <th className="px-5 py-3.5 text-right">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {!users ? (
              <tr><td colSpan={6} className="px-5 py-8 text-slate-500">Yükleniyor...</td></tr>
            ) : users.map((u) => {
              const isMe = u.id === me?.id;
              const perms = u.permissions || ["all"];
              return (
                <tr key={u.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-5 py-3.5 font-medium">{u.contact_name}{isMe && <span className="text-slate-400 text-xs ml-1.5">(siz)</span>}</td>
                  <td className="px-5 py-3.5 text-slate-500">{u.contact_email}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-wrap gap-1">
                      {perms.includes("all") ? (
                        <Badge className="rounded-none bg-slate-900 text-white gap-1" data-testid={`full-perm-badge-${u.id}`}><ShieldCheck size={11} /> Tam Yetkili</Badge>
                      ) : (
                        perms.map((p) => <Badge key={p} variant="outline" className="rounded-none border-slate-300 text-slate-600 text-[11px]">{permLabel(p)}</Badge>)
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">{statusBadge(u)}</td>
                  <td className="px-5 py-3.5 text-slate-500">{fmtDate(u.created_at)}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1.5">
                      {!u.password_set && (
                        <Button size="sm" variant="outline" onClick={() => resend(u)} data-testid={`resend-invite-${u.id}`} title="Daveti yeniden gönder" className="rounded-none h-8 px-2.5 border-slate-200">
                          <MailPlus size={13} />
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openEdit(u)} disabled={isMe} data-testid={`edit-admin-${u.id}`} title="Düzenle" className="rounded-none h-8 px-2.5 border-slate-200">
                        <Pencil size={13} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => toggleActive(u)} disabled={isMe} data-testid={`toggle-admin-${u.id}`} title={u.is_active === false ? "Aktifleştir" : "Pasifleştir"} className="rounded-none h-8 px-2.5 border-slate-200">
                        {u.is_active === false ? <CheckCircle2 size={13} className="text-emerald-600" /> : <Ban size={13} className="text-amber-600" />}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setConfirmDelete(u)} disabled={isMe} data-testid={`delete-admin-${u.id}`} title="Sil" className="rounded-none h-8 px-2.5 border-slate-200 text-red-600 hover:bg-red-50">
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent aria-describedby={undefined} className="rounded-none max-w-lg" data-testid="admin-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{dialog?.mode === "create" ? "Yeni Kullanıcı Davet Et" : "Kullanıcıyı Düzenle"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Ad Soyad</Label>
                <Input value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} data-testid="admin-name-input" className="rounded-none h-10" />
              </div>
              <div className="space-y-1.5">
                <Label>E-posta</Label>
                <Input value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} disabled={dialog?.mode === "edit"} data-testid="admin-email-input" className="rounded-none h-10" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Yetkiler</Label>
              <PermPicker perms={form.permissions} setPerms={(p) => setForm((f) => ({ ...f, permissions: p }))} />
            </div>
            <Button onClick={submit} disabled={busy || !form.contact_name.trim() || !form.contact_email.trim() || form.permissions.length === 0} data-testid="admin-user-submit" className="w-full rounded-none bg-slate-900 text-white hover:bg-slate-700 h-11 font-semibold">
              {busy ? "Gönderiliyor..." : dialog?.mode === "create" ? "Davet Et" : "Kaydet"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Kullanıcı silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.contact_name} ({confirmDelete?.contact_email}) kalıcı olarak silinecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-admin" className="rounded-none bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ViewersSection />
      <AssignersSection />
      <ProducersSection />
      <TaseronsSection />
    </div>
  );
}
